import { PlayerStatus } from '@tunein/web-player-manager';
import { noop } from 'es-toolkit';
import { api } from 'src/client/api';
import { buildConsentData } from 'src/client/player/utils';
import { storeSingleton } from 'src/client/store/storeSingleton';
import config from 'src/common/config';
import { hidePopoutPlayerDialogCookie } from 'src/common/constants/cookies';
import {
  OPEN_IN_EMBED_DIALOG_FREQUENCY,
  OPEN_IN_EMBED_ENABLED,
} from 'src/common/constants/experiments/popout';
import { selectIsDiscord, selectOneTrust, selectTuneInUserSerial } from 'src/common/selectors/app';
import { selectExperiment } from 'src/common/selectors/config';
import { getClientAppServices } from 'src/common/services';
import { getCookie, setCookie } from 'src/common/utils/cookie';
import openStandardWindow from 'src/common/utils/window/openStandardWindow';
import {
  POPUP_PLAYER_READY,
  RECONNECT_POPUP_PLAYER,
  UPDATE_POPUP_PLAYER,
  UPDATE_POPUP_PLAYER_CONSENT,
} from './constants/message';

const { insecureEmbedOrigin } = config;

const externalUrlPopOutDimensions = {
  width: 1180,
  height: 900,
};
const standardPopoutDimensions = {
  width: 640,
  height: 100,
};

export class PopoutPlayer {
  #popoutWindow = null;
  #guideId = null;
  #isPremium = null;
  #partnerId = '';
  #serial = '';
  #externalPlayerUrl = null;
  #consentData;
  #player;

  get isEnabled() {
    const state = storeSingleton.state;
    const openInEmbedEnabled = selectExperiment(state, OPEN_IN_EMBED_ENABLED);
    const isDiscord = selectIsDiscord(state);

    return openInEmbedEnabled && !isDiscord;
  }

  async init(player) {
    this.#player = player;
    window.addEventListener('message', this.#handlePopoutMessage);
    this.#loadConsentData().catch(noop);
    this.#watchConsent();

    player.on('error', (event) => {
      this.#externalPlayerUrl = null;

      if (
        popoutPlayer.isEnabled &&
        event.error?.fatal &&
        event.isLastSource &&
        event.hadOnlyHttpStreams
      ) {
        popoutPlayer.promptUser();
      }
    });

    player.on('externalplayerurl', (url) => {
      this.#externalPlayerUrl = url;

      popoutPlayer.promptUser();
    });

    player.on('playing', () => {
      if (this.#isOpen) {
        this.#close();
      }
    });
  }

  promptUser() {
    const { playerStore } = getClientAppServices().stores;
    const { setPopoutState, setPlayerStatus } = playerStore.getState();

    setPlayerStatus(PlayerStatus.IDLE);

    if (getCookie(hidePopoutPlayerDialogCookie.name)) {
      setPopoutState({ showButton: true, isDialogOpen: false });
      return;
    }

    const frequency = api.getConfigSetting(OPEN_IN_EMBED_DIALOG_FREQUENCY, 1);

    setCookie(hidePopoutPlayerDialogCookie.name, frequency);

    setPopoutState({ showButton: true, isDialogOpen: true });
  }

  openOrUpdate() {
    const previousGuideId = this.#guideId;
    const { playerStore } = getClientAppServices().stores;
    const { currentGuideItem } = playerStore.getState();

    this.#guideId = currentGuideItem.playableGuideId;
    this.#isPremium = api.isPremium;
    this.#partnerId = api.partnerId;
    // TODO: consider exposing serial on WAC
    this.#serial = selectTuneInUserSerial(storeSingleton.state);

    if (this.#externalPlayerUrl) {
      openStandardWindow({
        url: this.#externalPlayerUrl,
        ...externalUrlPopOutDimensions,
      });
      return;
    }

    this.#isOpen ? this.#send(previousGuideId) : this.#open();
  }

  // Consent can take a while to resolve (in GDPR regions, until the banner is answered), so the
  // popout gets it in a later update rather than waiting for it.
  async #loadConsentData() {
    this.#consentData = await buildConsentData();

    if (this.#isOpen) {
      this.#postConsent();
    }
  }

  // Only GDPR listeners get a page reload on a consent change, which drops this page's handle on
  // the popout. Everywhere else the popout stays connected, so it hears the new choice here.
  #watchConsent() {
    let oneTrust = selectOneTrust(storeSingleton.state);

    storeSingleton.subscribe(() => {
      const previousOneTrust = oneTrust;
      oneTrust = selectOneTrust(storeSingleton.state);

      if (previousOneTrust !== oneTrust) {
        this.#loadConsentData().catch(noop);
      }
    });
  }

  get #isOpen() {
    return Boolean(this.#popoutWindow && !this.#popoutWindow.closed);
  }

  get #embedUrl() {
    return `${insecureEmbedOrigin}/embed/player/${this.#guideId}?mode=popout&partnerId=${this.#partnerId}&serial=${this.#serial}`;
  }

  #focus() {
    this.#popoutWindow?.focus();
  }

  #handlePopoutMessage = (event) => {
    const { data, origin, source } = event;

    if (origin !== insecureEmbedOrigin) {
      return;
    }

    // The popout announces itself once it loads, and expects the listener back.
    if (data?.type === POPUP_PLAYER_READY && this.#popoutWindow) {
      this.#send();
      return;
    }

    // A reload of this page, which OneTrust does when a GDPR listener changes consent, drops its
    // handle on the popout it opened. The popout keeps announcing itself, so it can be picked up
    // again and given the consent the listener has now.
    if (data?.type === RECONNECT_POPUP_PLAYER && source?.opener === window) {
      this.#popoutWindow = source;
      this.#postConsent();
    }
  };

  #openWindow({ width, height }) {
    const { screenLeft, screenX, innerWidth } = window;

    const attributeEntries = Object.entries({
      width,
      height,
      top: 0,
      left: (screenLeft || screenX) + (innerWidth - width),
    });
    const attributes = attributeEntries.map((entry) => entry.join('=')).join(',');

    return window.open(this.#embedUrl, '_blank', attributes);
  }

  #open() {
    this.#popoutWindow = this.#openWindow(standardPopoutDimensions);
  }

  #send(previousGuideId) {
    this.#focus();

    if (previousGuideId && previousGuideId !== this.#guideId) {
      this.#popoutWindow?.location.replace(this.#embedUrl);
      return;
    }

    this.#postUpdate();
  }

  /**
   * Tells the popout about the listener: premium listeners hear no prerolls, and a preroll already
   * played here isn't played again there. Its ads and Unified Events follow this page's consent.
   */
  #postUpdate() {
    const payload = {
      type: UPDATE_POPUP_PLAYER,
      isPremium: this.#isPremium,
      prerollPlayed: this.#player.didPlayPreroll,
      consentData: this.#consentData,
      privacy: api.reportingData.context.privacy,
    };

    this.#popoutWindow?.postMessage(payload, insecureEmbedOrigin);
  }

  /** Only the consent, which can change after the popout has settled its prerolls. */
  #postConsent() {
    // Until consent has loaded, the privacy block is only the defaults, which would replace what
    // the popout has. The load posts it once it finishes.
    if (!this.#consentData) {
      return;
    }

    const payload = {
      type: UPDATE_POPUP_PLAYER_CONSENT,
      consentData: this.#consentData,
      privacy: api.reportingData.context.privacy,
    };

    this.#popoutWindow?.postMessage(payload, insecureEmbedOrigin);
  }

  #close() {
    this.#popoutWindow?.close();
  }
}

export const popoutPlayer = new PopoutPlayer();
